// combatpro/monster/monsterEngine.js
//
// Combat PRO - Monster Runtime (PURE engine).
//
// STAGE 38 (Monster Runtime, Option A - state foundation only):
//   Pure, data-in / data-out monster helpers extracted 1:1 from the master
//   spec battletracker.html. NO React, NO DOM, NO context, NO state, NO
//   history, NO rendering, NO side effects. Zero imports.
//
// In scope for THIS stage (only):
//   createMonster()                       default monster object shape
//   deriveTabName(monster, index)         tab display-name derivation
//   parseHpFromRange(hpRange)             leading-integer HP parse
//   hpPercentAndCondition(currentHP, maxHP)  HP percent + condition tier
//   cloneMonsterData(source, copyIndex)   deep clone + name auto-increment
//
// Explicitly DEFERRED (NOT in this stage): createMonsterPanel, updatePanel,
// switchMonster remount behaviour, Conditions / Morale / Spells / Legendary /
// Lair runtimes, Encounter save/cloud/log, Initiative integration. The monster
// object carries nested arrays (abilities, weapons, legendaryActions,
// lairActions, spells, saves, abilityScores, environment, lastActions) for those
// runtimes as INERT DATA only. Nothing here reads or operates on them.
//
// Source-of-truth lines in battletracker.html (master spec, read-only):
//   factory      5579-5624  (resetAllData, the "NEW HP system" canonical shape:
//                            maxHP / currentHP, not the stale 4984 totalHp shape)
//   tab name     7564-7585  (updateTabName, DOM stripped)
//   hp parse     6694-6707  (selectMonster auto-HP, /^(\d+)/)
//   hp condition 7826-7857  (updatePanel, DOM stripped, data only)
//   clone data   8880-8902  (cloneMonster, DOM + orchestration stripped)

// Default monster object. Mirrors the spec's "NEW HP system" factory (5579)
// field-for-field, value-for-value. This is the live runtime shape: HP logic
// reads currentHP / maxHP (updatePanel 7828-7830), confirming this shape over
// the stale initial-declaration shape (4984: totalHp / damageTaken).
export function createMonster() {
  return {
    customName: "",
    selectedMonster: "",
    size: "Medium",
    race: "",
    class: "",
    language: "",
    ac: 0,
    passivePerception: 0,
    spellDC: 0,
    hpRange: "",
    cr: "",
    initiative: 0,
    maxHP: 0,
    currentHP: 0,
    saves: { str: 0, dex: 0, con: 0, int: 0, wis: 0, cha: 0 },
    abilityScores: { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 },
    immunities: "",
    resistances: "",
    abilities: [],
    weapons: [],
    legendaryActions: [],
    maxLegendaryActions: 3,
    usedLegendaryActions: 0,
    legendaryResetRate: "turn",
    morale: 100,
    moraleThreshold: 30,
    isLeader: false,
    lairActions: [],
    lairEnabled: false,
    environment: {
      darkness: false,
      dimLight: false,
      difficultTerrain: false,
      hazard: false
    },
    speed: "30 ft",
    notes: "",
    spells: [
      { name: "", castRound: 1 },
      { name: "", castRound: 1 },
      { name: "", castRound: 1 }
    ],
    effect: "",
    effectStartRound: 1,
    lastActions: []
  };
}

// Tab display name. Priority: customName (trimmed, non-empty) > selectedMonster
// > "Monster N+1". Names longer than 18 chars are truncated to 18 + "...".
// Mirrors updateTabName (7564-7585) with the DOM read/write removed; returns the
// derived string instead of writing tabNameEl.textContent.
export function deriveTabName(monster, index) {
  let displayName = '';

  if (monster.customName && monster.customName.trim()) {
    displayName = monster.customName.trim();
  } else if (monster.selectedMonster) {
    displayName = monster.selectedMonster;
  } else {
    displayName = 'Monster ' + (index + 1);
  }

  if (displayName.length > 18) {
    displayName = displayName.substring(0, 18) + '...';
  }

  return displayName;
}

// Full display name with NO truncation. Same priority as deriveTabName
// (customName > selectedMonster > "Monster N+1") but never shortened, for every
// place that shows the CURRENT selected monster or a target value (Active Monster,
// monster tabs, spell targets) where a clipped name is not acceptable.
export function deriveFullName(monster, index) {
  if (monster && monster.customName && monster.customName.trim()) { return monster.customName.trim(); }
  if (monster && monster.selectedMonster) { return String(monster.selectedMonster); }
  return 'Monster ' + (index + 1);
}

// Parse the leading integer out of an hpRange string like "45 (6d10 + 12)".
// Returns that integer, or null when there is no leading-digit match (or no
// input). Mirrors the selectMonster auto-HP parse (6694-6707): /^(\d+)/ then
// parseInt. The caller decides what to do with null.
export function parseHpFromRange(hpRange) {
  if (!hpRange) return null;
  const hpMatch = hpRange.match(/^(\d+)/);
  if (!hpMatch) return null;
  return parseInt(hpMatch[1], 10);
}

// Compute HP percentage and condition tier from current / max HP. Pure data
// only - no DOM, no bar width writes, no badge innerHTML, no tab colour, no
// spell / effect / morale side effects (all of that stays in updatePanel and
// is deferred). Mirrors the condition-tier logic at 7828-7857:
//   remaining = currentHP || 0
//   maxHp     = maxHP || 0
//   percent   = maxHp > 0 ? (remaining / maxHp) * 100 : 100
//   Dead      : remaining === 0 && maxHp > 0
//   Critical  : percent <= 25 && percent > 0
//   Bloodied  : percent <= 50 && percent > 25
//   Healthy   : otherwise
// barClass mirrors the hp-bar modifier the spec adds (dead / critical /
// bloodied; none for healthy) so a future renderer need not re-derive it.
export function hpPercentAndCondition(currentHP, maxHP) {
  const remaining = currentHP || 0;
  const maxHp = maxHP || 0;
  const percent = maxHp > 0 ? (remaining / maxHp) * 100 : 100;

  let condition = "Healthy";
  let conditionClass = "condition-healthy";
  let barClass = "";

  if (remaining === 0 && maxHp > 0) {
    condition = "Dead";
    conditionClass = "condition-dead";
    barClass = "dead";
  } else if (percent <= 25 && percent > 0) {
    condition = "Critical";
    conditionClass = "condition-critical";
    barClass = "critical";
  } else if (percent <= 50 && percent > 25) {
    condition = "Bloodied";
    conditionClass = "condition-bloodied";
    barClass = "bloodied";
  }

  return { remaining, maxHp, percent, condition, conditionClass, barClass };
}

// Produce one cloned monster data object from a source monster. Pure: deep
// clones via JSON round-trip, auto-increments the name, and resets initiative
// and usedLegendaryActions. copyIndex is the zero-based offset of this copy
// within a clone batch (maps to the spec loop variable i at 8884-8902); it
// drives the name increment exactly as the spec does. No DOM, no slot finding,
// no panel/tab updates, no combat log, no switchMonster (all deferred).
//   name source : customName || selectedMonster || "Monster"
//   trailing N  : baseNum + copyIndex + 1, substituted via /\d+$/
//   no trailing : name + " " + (copyIndex + 2)
export function cloneMonsterData(source, copyIndex) {
  const sourceName = source.customName || source.selectedMonster || 'Monster';
  const clone = JSON.parse(JSON.stringify(source));

  const numberMatch = sourceName.match(/(\d+)$/);
  if (numberMatch) {
    const baseNum = parseInt(numberMatch[1], 10);
    const newNum = baseNum + copyIndex + 1;
    clone.customName = sourceName.replace(/\d+$/, newNum);
  } else {
    clone.customName = sourceName + ' ' + (copyIndex + 2);
  }

  clone.initiative = 0;
  clone.usedLegendaryActions = 0;

  return clone;
}

// Normalize a stored / imported monster record onto the live runtime shape.
//
// Every Combat PRO page reads the createMonster() shape directly (spells[slot],
// abilityScores.dex, saves.str, weapons.map, ...). Records written into the
// encounter document from outside the Combat PRO runtime - the generator
// "send to Combat PRO" import (backend routes/desktop.py _prepared_empty_combat /
// _prepared_monster) stores name-only stubs {customName, selectedMonster} or an
// allowlisted subset - lack those fields, and opening Spells / Monsters on such
// an encounter threw inside a state updater and unmounted the whole app.
//
// This fills every MISSING or wrong-typed field from createMonster() and keeps
// every value already present (including fields createMonster does not know,
// e.g. the namespaced rt runtime object). Nested default objects (saves,
// abilityScores, environment) are merged key-by-key. spells is padded to the
// three runtime slots; a bare spell-name string becomes {name, castRound: 1}.
// Pure: no DOM, no state, no side effects; the input is not mutated.
export function normalizeMonster(source) {
  var base = createMonster();
  if (!source || typeof source !== 'object' || Array.isArray(source)) { return base; }
  var out = Object.assign({}, source);
  Object.keys(base).forEach(function (key) {
    var def = base[key];
    var cur = out[key];
    if (Array.isArray(def)) {
      if (!Array.isArray(cur)) { out[key] = def; }
      return;
    }
    if (def !== null && typeof def === 'object') {
      out[key] = (cur && typeof cur === 'object' && !Array.isArray(cur)) ? Object.assign({}, def, cur) : def;
      return;
    }
    if (cur === undefined || cur === null || typeof cur !== typeof def) {
      if (typeof def === 'number' && typeof cur === 'string' && cur.trim() !== '' && !isNaN(Number(cur))) {
        out[key] = Number(cur);
      } else if (typeof def === 'string' && typeof cur === 'number') {
        out[key] = String(cur);
      } else {
        out[key] = def;
      }
    }
  });
  var spells = out.spells.map(function (slot) {
    if (typeof slot === 'string') { return { name: slot, castRound: 1 }; }
    if (!slot || typeof slot !== 'object') { return { name: '', castRound: 1 }; }
    var s = Object.assign({}, slot);
    if (typeof s.name !== 'string') { s.name = ''; }
    if (typeof s.castRound !== 'number') { s.castRound = 1; }
    return s;
  });
  while (spells.length < base.spells.length) { spells.push({ name: '', castRound: 1 }); }
  out.spells = spells;
  return out;
}
